import type { KeyboardEvent } from 'react';

import { useQueryBuilderContext } from '../context';
import { focusAdjacentGap, focusQueryEdge, getFocusableCells } from '../utils';

import type { GridSelectionState } from './useGridSelection';
import { useKeyboardSelectionContext } from './useKeyboardSelection';

/**
 * True for the empty auto-inserted `ft-after-N` spacer that sits between two
 * adjacent structured tokens. These are transparent to sequential arrow-nav so
 * crossing one lozenge to the next takes a single keypress. Excludes `ft-start`
 * (leading), the trailing chain input (`data-trailing`), and any spacer the user
 * has typed into (`data-has-value`) — those remain real landing spots.
 */
function isEmptyBetweenTokenCell(el: HTMLElement): boolean {
  return (
    el.hasAttribute('data-freetext-token') &&
    !el.hasAttribute('data-has-value') &&
    !el.hasAttribute('data-trailing') &&
    /^ft-after-\d+$/.test(el.getAttribute('data-key') ?? '')
  );
}

/**
 * Provides keyboard navigation props for a single token row inside the FilterBar grid.
 *
 * - ArrowLeft / ArrowRight: moves focus to the previous/next focusable cell within
 *   the token (key → op → value), crossing token boundaries when at the edge.
 * - Notifies the grid's GridSelectionState of the focused token so virtual focus tracks it.
 *
 * @param tokenKey - The stable key identifying this token in the grid.
 * @param state    - The GridSelectionState from FilterBar; may be null when used standalone.
 */
export function useQueryBuilderGridItem(
  tokenKey: string,
  state: GridSelectionState | null,
  ariaLabel?: string,
): {
  containerProps: React.HTMLAttributes<HTMLElement> & { 'data-key': string; role?: 'row' };
  cellProps: { role?: 'gridcell' };
} {
  const { wrapperRef } = useQueryBuilderContext();
  const selCtx = useKeyboardSelectionContext();

  /**
   * Returns the search root for focusable-element traversal.
   * Priority:
   *  1. Nearest [role="grid"] ancestor — set by FilterBar (render-prop pattern).
   *  2. wrapperRef — set by <QueryBuilder> (static-children pattern).
   */
  function getRoot(fromContainer: HTMLElement): HTMLElement | null {
    return fromContainer.closest<HTMLElement>('[role="grid"]') ?? wrapperRef.current;
  }

  /**
   * Focus the immediately adjacent focusable element in the grid that follows
   * currentContainer in DOM order (FreeTextToken, logic-operator button, or next
   * lozenge's first cell — nothing is skipped).
   */
  function focusNextToken(currentContainer: HTMLElement): boolean {
    const root = getRoot(currentContainer);
    if (!root) {
      return false;
    }
    const allFocusable = getFocusableCells(root);
    const next = allFocusable.find(
      (el) =>
        !currentContainer.contains(el) &&
        !isEmptyBetweenTokenCell(el) &&
        // eslint-disable-next-line no-bitwise
        !!(currentContainer.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
    );
    if (next) {
      next.focus();
      return true;
    }
    return false;
  }

  /**
   * Focus the immediately adjacent focusable element in the grid that precedes
   * currentContainer in DOM order.
   */
  function focusPreviousToken(currentContainer: HTMLElement): boolean {
    const root = getRoot(currentContainer);
    if (!root) {
      return false;
    }
    const allFocusable = getFocusableCells(root);
    const candidates = allFocusable.filter(
      (el) =>
        !currentContainer.contains(el) &&
        !isEmptyBetweenTokenCell(el) &&
        // eslint-disable-next-line no-bitwise
        !!(currentContainer.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING),
    );
    if (candidates.length) {
      candidates[candidates.length - 1].focus();
      return true;
    }
    return false;
  }

  // Only apply grid ARIA roles and keyboard handlers when inside a grid context
  // (state is null when LozengeFilter is used standalone without a tokens-based FilterBar).
  const role = state ? ('row' as const) : undefined;

  const containerProps = {
    ...(role ? { role, ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}) } : {}),
    ...(state ? { 'aria-selected': state.isSelected(tokenKey) } : {}),
    'data-key': tokenKey,
    'data-token-container': '',

    onFocus() {
      state?.setFocusedKey(tokenKey);
    },

    onKeyDownCapture(e: KeyboardEvent<HTMLElement>) {
      // Shift+Arrow initiates token selection, but only when the cursor is already
      // at the boundary of an input (position 0 for left, value.length for right).
      // When mid-input, let the browser handle text selection normally.
      if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && state && selCtx) {
        const target = e.target as HTMLElement;
        if (target instanceof HTMLInputElement) {
          const atBoundary =
            e.key === 'ArrowLeft'
              ? target.selectionStart === 0 && target.selectionEnd === 0
              : target.selectionStart === target.value.length && target.selectionEnd === target.value.length;
          if (!atBoundary) {
            return;
          }
        }
        e.preventDefault();
        selCtx.selectInDirection(e.key === 'ArrowLeft' ? 'left' : 'right', tokenKey);
        return;
      }

      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }

      // Modified arrows are grid-level jumps, not native caret movement:
      //  - Cmd/Ctrl+←/→ jumps to the very start/end of the whole query.
      //  - Alt+←/→ hops to the previous/next free-text gap between lozenges.
      // Inside an editable cell input, native caret/word/line editing must win
      // (Cmd+← = line start, Alt+← = word left, …); only treat the modified arrow
      // as a grid jump once the caret is already at the cell's boundary — otherwise
      // this capture-phase handler would preempt the input's default behavior and
      // break modifier+arrow editing in key/op/value cells.
      if (state && (e.metaKey || e.ctrlKey || e.altKey)) {
        const target = e.target as HTMLElement;
        if (target instanceof HTMLInputElement) {
          // Require a collapsed caret at the edge (matching the Shift+Arrow branch and
          // FreeTextToken): a non-collapsed selection anchored at the start still needs
          // native word/line movement, so don't hijack it as a grid jump.
          const atBoundary =
            e.key === 'ArrowLeft'
              ? target.selectionStart === 0 && target.selectionEnd === 0
              : target.selectionStart === target.value.length && target.selectionEnd === target.value.length;
          if (!atBoundary) {
            return;
          }
        }
        const containerEl = e.currentTarget as HTMLElement;
        const root = getRoot(containerEl);
        const dir = e.key === 'ArrowLeft' ? 'left' : 'right';
        const moved =
          root && (e.metaKey || e.ctrlKey ? focusQueryEdge(root, dir) : focusAdjacentGap(root, containerEl, dir));
        if (moved) {
          e.preventDefault();
        }
        return;
      }

      // If this token's autocomplete popover has a virtually-focused option,
      // block grid navigation and let the bubble-phase handler clear the focus.
      const container = e.currentTarget as HTMLElement;
      if (container.getAttribute('data-autocomplete-active') === 'true') {
        e.preventDefault();
        return;
      }

      const target = e.target as HTMLElement;
      const cells = getFocusableCells(container);
      const idx = cells.indexOf(target);

      if (e.key === 'ArrowRight') {
        // If there's a next cell within this token, move to it.
        if (idx < cells.length - 1) {
          // But if the target is an input, only leave when the cursor is at the end.
          if (target instanceof HTMLInputElement && target.selectionStart !== target.value.length) {
            return;
          }
          e.preventDefault();
          cells[idx + 1].focus();
          return;
        }
        // At the last cell — try crossing to next token.
        // For inputs, only cross when the cursor is already at the end.
        if (target instanceof HTMLInputElement && target.selectionStart !== target.value.length) {
          return;
        }
        if (focusNextToken(container)) {
          e.preventDefault();
        }
        return;
      }

      // ArrowLeft
      if (idx > 0) {
        // If the target is an input, only leave when the cursor is at the start.
        if (target instanceof HTMLInputElement && target.selectionStart !== 0) {
          return;
        }
        e.preventDefault();
        cells[idx - 1].focus();
        return;
      }
      // At the first cell — try crossing to previous token.
      // For inputs, only cross when the cursor is already at the start.
      if (target instanceof HTMLInputElement && target.selectionStart !== 0) {
        return;
      }
      if (focusPreviousToken(container)) {
        e.preventDefault();
      }
    },
  };

  const cellProps = state ? { role: 'gridcell' as const } : {};

  return { containerProps, cellProps };
}
